import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
} from '@mui/material';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { geofenceService } from '../../services/geofence.service';
import { StatusChip } from '../../components/common/StatusChip';
import { LoadingSkeleton } from '../../components/common/LoadingSkeleton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { GeofenceMap } from '../../components/maps/GeofenceMap';
import type { Geofence } from '../../types';

export function GeofencesPage() {
  const [editing, setEditing] = useState<Geofence | null>(null);
  const [radius, setRadius] = useState(100);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofenceService.getAll({ page: 1, limit: 100 }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, radiusMeters }: { id: string; radiusMeters: number }) =>
      geofenceService.update(id, { radius: radiusMeters }),
    onSuccess: () => {
      toast.success('Geofence updated');
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setEditing(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const geofences = data?.data ?? [];

  return (
    <Box>
      <PageHeader
        compact
        title="Geofences"
        subtitle="Customer location boundaries for arrival detection"
      />

      {isLoading ? (
        <LoadingSkeleton variant="cards" cardCount={6} />
      ) : geofences.length === 0 ? (
        <Card>
          <EmptyState
            title="No geofences yet"
            description="Geofences are created automatically when you add a customer with a location pin and radius."
            icon={<MyLocationRoundedIcon sx={{ fontSize: 32, color: 'neutral.400' }} />}
          />
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {geofences.map((g) => (
            <Grid item xs={12} sm={6} lg={4} key={g.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                    '&:hover': { transform: 'none' },
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap letterSpacing="-0.01em">
                        {g.customer?.name ?? 'Customer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Arrival detection zone
                      </Typography>
                    </Box>
                    <StatusChip status={g.isActive ? 'ACTIVE' : 'INACTIVE'} />
                  </Stack>

                  <Box mb={1.5}>
                    <GeofenceMap
                      latitude={g.latitude}
                      longitude={g.longitude}
                      radius={g.radius}
                      height={160}
                    />
                  </Box>

                  <Stack spacing={0.75}>
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" fontWeight={600} color="text.primary">Center</Box>
                      {' · '}
                      {g.latitude.toFixed(5)}, {g.longitude.toFixed(5)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" fontWeight={600} color="text.primary">Radius</Box>
                      {' · '}
                      {g.radius} meters
                    </Typography>
                  </Stack>
                </CardContent>
                <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => {
                      setEditing(g);
                      setRadius(g.radius);
                    }}
                  >
                    Adjust radius
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust geofence radius</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {editing?.customer?.name ?? 'Customer'} — drag the slider to set the arrival zone size
            </Typography>
            {editing && (
              <Box mb={2}>
                <GeofenceMap
                  latitude={editing.latitude}
                  longitude={editing.longitude}
                  radius={radius}
                  height={240}
                  interactive
                />
              </Box>
            )}
            <Typography variant="body2" gutterBottom>
              Radius: <strong>{radius}m</strong>
            </Typography>
            <Slider
              value={radius}
              onChange={(_, v) => setRadius(v as number)}
              min={30}
              max={500}
              step={10}
              valueLabelDisplay="auto"
              aria-label="Geofence radius in meters"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={updateMutation.isPending}
            onClick={() => editing && updateMutation.mutate({ id: editing.id, radiusMeters: radius })}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
